import { useQuery } from '@tanstack/react-query'

import apiClient from '../api/client'

export interface Branding {
  product_name: string
  primary_color: string
  logo_url: string        // empty when no logo uploaded
  custom_css: string
  invoice_footer: string
}

// useBranding reads /api/v1/branding/public once per app boot and
// caches the bundle. Login, Setup and the Admin/Portal layouts all
// pull from this hook so branding changes take effect after refresh.
export function useBranding() {
  return useQuery<Branding>({
    queryKey: ['branding', 'public'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Branding }>(
        '/api/v1/branding/public')
      return res.data.data
    },
    staleTime: 5 * 60 * 1000,
  })
}

// brandingApply mounts the branding into the DOM: sets the
// --branding-accent CSS variable + injects the custom_css block.
// Called once from App.tsx on every branding-data change.
export function applyBranding(b: Branding | undefined) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (b?.primary_color) {
    root.style.setProperty('--branding-accent', b.primary_color)
  } else {
    root.style.removeProperty('--branding-accent')
  }
  // Custom CSS lives in a single <style id="nc-branding"> tag so we
  // can update it idempotently without leaking styles.
  let tag = document.getElementById('nc-branding') as HTMLStyleElement | null
  if (b?.custom_css && b.custom_css.trim() !== '') {
    if (!tag) {
      tag = document.createElement('style')
      tag.id = 'nc-branding'
      document.head.appendChild(tag)
    }
    tag.textContent = b.custom_css
  } else if (tag) {
    tag.remove()
  }
}
